import { forwardRef } from "react";
import { cn } from "src/shadcn/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/primitives/Popover";
import { SelectFieldWrapper } from "../Select/SelectFieldWrapper";
import { Tree } from "../Tree";
import { useTreeSelectState } from "./hooks/useTreeSelectState";
import { TreeSelectTrigger } from "./TreeSelectTrigger";
import { SIZE_CONFIG, DROPDOWN_HEIGHT } from "./constants";
import type { TreeSelectProps } from "./types";

const TreeSelect = forwardRef<HTMLButtonElement, TreeSelectProps>(
  (props, ref) => {
    const {
      id,
      label,
      error,
      helpText,
      errorId,
      helpTextId,
      isDisabled,
      required,
      isOpen,
      handleOpenChange,
      currentValue,
      handleClear,
      selectedLabel,
      showClearButton,
      size,
      isSearchable,
      searchTerm,
      setSearchTerm,
      prunedTreeData,
      effectiveExpandedKeys,
      handleSelect,
      handleExpand,
    } = useTreeSelectState(props);
    const { suffixIcon, switcherIcon, autoExpandParent, className } = props;

    return (
      <SelectFieldWrapper
        id={id}
        label={label}
        error={error}
        helpText={helpText}
        errorId={errorId}
        helpTextId={helpTextId}
        isDisabled={isDisabled}
        required={required}
        className={className}
      >
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <TreeSelectTrigger
              ref={ref}
              currentValue={currentValue}
              error={error}
              handleClear={handleClear}
              isDisabled={isDisabled}
              placeholder={props.placeholder}
              selectedLabel={selectedLabel}
              showClearButton={showClearButton}
              size={size}
              suffixIcon={suffixIcon}
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
            onOpenAutoFocus={e => {
              if (isSearchable) e.preventDefault();
            }}
          >
            {isSearchable && (
              <div className="border-b border-border px-2 py-1.5">
                <input
                  autoFocus
                  type="text"
                  className={cn(
                    "w-full bg-transparent outline-none placeholder:text-muted-foreground",
                    SIZE_CONFIG[size].text
                  )}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            )}
            {prunedTreeData.length > 0 ? (
              <div className="p-1">
                <Tree
                  autoExpandParent={autoExpandParent}
                  expandedKeys={effectiveExpandedKeys}
                  height={DROPDOWN_HEIGHT}
                  switcherIcon={switcherIcon}
                  treeData={prunedTreeData}
                  onExpand={handleExpand}
                  onSelect={handleSelect}
                />
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No results found.
              </div>
            )}
          </PopoverContent>
        </Popover>
      </SelectFieldWrapper>
    );
  }
);

TreeSelect.displayName = "TreeSelect";

export { TreeSelect };
